// LandingPageManagement.jsx

"use client";

import { useEffect, useMemo, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import {
  Globe,
  Link,
  ExternalLink,
  Mail,
  Phone,
  Type,
  Image,
  Video,
  Palette,
  Save,
  Loader2,
  X,
  BadgeCheck,
  Flag,
  Plus,
  Trash2,
  Star,
} from "lucide-react";

const decodeJWT = (token) => {
  try {
    const payload = token.split(".")[1];
    const decodedPayload = atob(payload);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error("Erro ao decodificar JWT:", error);
    return null;
  }
};

const unwrapApiData = (res) => res?.data?.data ?? res?.data ?? null;

const extractErrorPayload = (err) =>
  err?.response?.data ?? { message: "Unexpected error" };

const parseErrorMap = (payload) => {
  if (!payload) return null;

  if (payload?.message && typeof payload.message === "object") {
    return payload.message;
  }

  if (payload?.data && typeof payload.data === "object") {
    return payload.data;
  }

  return null;
};

const getAuthContextFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded) return null;

  const userRoleId = Number(decoded.user_role_id ?? decoded.role_id ?? 0);

  const partnerCompanyId = Number(
    decoded.partner_company_id ??
      decoded.company_id ??
      decoded.partnerCompanyId ??
      decoded.companyId ??
      0
  );

  return {
    token,
    decoded,
    userRoleId: Number.isNaN(userRoleId) ? null : userRoleId,
    partnerCompanyId: Number.isNaN(partnerCompanyId) ? null : partnerCompanyId,
  };
};

const safeJsonParse = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const asBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "TRUE" ||
  value === "True" ||
  value === "on" ||
  value === "ON" ||
  value === "yes" ||
  value === "YES";

const toStringBool = (value) => (asBool(value) ? "true" : "false");

/**
 * Normaliza o landing_config para o modelo desejado:
 * {
 *  email, phone, partner_company_name,
 *  landing_headline, landing_subheadline,
 *  form_quote_headline, form_banner_enabled, form_badge_enabled,
 *  landing_logo_url, landing_video_url, landing_favicon_url,
 *  landing_primary_color, landing_primary_hover_color,
 *  landing_secondary_color, landing_secondary_hover_color,
 *  landing_background_image_url,
 *  reviews: [{ author_name, rating, text }]
 * }
 *
 * Também mapeia legacy keys:
 * - form_banner_active -> form_banner_enabled
 * - form_badge_active  -> form_badge_enabled
 */
const ensureLandingConfigShape = (rawCfg) => {
  const cfg = rawCfg || {};

  // reviews pode vir como array ou como string JSON
  let reviewsRaw = [];
  if (Array.isArray(cfg?.reviews)) {
    reviewsRaw = cfg.reviews;
  } else if (typeof cfg?.reviews === "string") {
    try {
      const parsed = JSON.parse(cfg.reviews);
      if (Array.isArray(parsed)) reviewsRaw = parsed;
    } catch {
      reviewsRaw = [];
    }
  }

  const normalizedReviews = (Array.isArray(reviewsRaw) ? reviewsRaw : [])
    .map((r) => {
      const author_name = String(
        r?.author_name ?? r?.name ?? r?.author ?? ""
      ).trim();
      const text = String(r?.text ?? r?.comment ?? r?.message ?? "").trim();
      const rawRating = Number(r?.rating ?? r?.stars ?? r?.rate ?? 0);
      const rating = rawRating >= 1 && rawRating <= 5 ? rawRating : 5;

      return { author_name, rating, text };
    })
    .filter((r) => r.author_name || r.text);

  const normalized = {
    partner_company_name: cfg?.partner_company_name ?? "",
    email: cfg?.email ?? "",
    phone: cfg?.phone ?? "",

    landing_headline: cfg?.landing_headline ?? "",
    landing_subheadline: cfg?.landing_subheadline ?? "",

    form_quote_headline: cfg?.form_quote_headline ?? "",

    // compatibilidade: aceitar *_active e normalizar pra *_enabled
    form_banner_enabled:
      cfg?.form_banner_enabled ??
      cfg?.form_banner_active ??
      cfg?.form_banner ??
      "false",
    form_badge_enabled:
      cfg?.form_badge_enabled ??
      cfg?.form_badge_active ??
      cfg?.form_badge ??
      "false",

    landing_logo_url: cfg?.landing_logo_url ?? "",
    landing_video_url: cfg?.landing_video_url ?? "",
    landing_favicon_url: cfg?.landing_favicon_url ?? "",

    landing_primary_color: cfg?.landing_primary_color ?? "#0B1220",
    landing_primary_hover_color: cfg?.landing_primary_hover_color ?? "",
    landing_secondary_color: cfg?.landing_secondary_color ?? "#00A3FF",
    landing_secondary_hover_color: cfg?.landing_secondary_hover_color ?? "",

    landing_background_image_url: cfg?.landing_background_image_url ?? "",

    // ✅ REVIEWS (testimonials)
    reviews: normalizedReviews,
  };

  // Preserve quaisquer campos extras que existam
  // (mas sem sobrescrever os normalizados acima)
  return { ...cfg, ...normalized };
};

/**
 * Antes de enviar para o backend, garante que:
 * - *_enabled sejam strings "true"/"false" (como no exemplo do modelo).
 * - reviews venha como array com rating numérico 1..5
 */
const normalizeLandingConfigForSave = (cfg) => {
  const shaped = ensureLandingConfigShape(cfg || {});

  const rawReviews = Array.isArray(shaped.reviews) ? shaped.reviews : [];
  const reviews = rawReviews
    .map((r) => {
      const author_name = String(r?.author_name ?? "").trim();
      const text = String(r?.text ?? "").trim();
      const rawRating = Number(r?.rating ?? 0);
      const rating = rawRating >= 1 && rawRating <= 5 ? rawRating : 5;

      return { author_name, rating, text };
    })
    // permite salvar [] para ocultar a seção no site
    .filter((r) => r.author_name || r.text);

  return {
    ...shaped,
    reviews,
    form_banner_enabled: toStringBool(shaped.form_banner_enabled),
    form_badge_enabled: toStringBool(shaped.form_badge_enabled),
  };
};

function FieldError({ error }) {
  if (!error) return null;
  const text = Array.isArray(error) ? error.join(" ") : String(error);
  return <div className="text-red-400 text-xs mt-2">{text}</div>;
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  Icon,
  error,
  type = "text",
  disabled = false,
  required = false,
  clearable = true,
}) {
  return (
    <div>
      <label className="text-sm font-medium text-white mb-2 block">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Icon className="text-gray-400" size={18} />
          </div>
        ) : null}

        <input
          type={type}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full ${
            Icon ? "pl-10" : "pl-3"
          } pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all ${
            disabled ? "opacity-70 cursor-not-allowed" : ""
          }`}
        />

        {clearable && !disabled && value ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange({ target: { value: "" } });
            }}
            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
            title="Clear"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <FieldError error={error} />
    </div>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  Icon,
  error,
  disabled = false,
  required = false,
  clearable = true,
  rows = 4,
}) {
  return (
    <div>
      <label className="text-sm font-medium text-white mb-2 block">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <div className="absolute top-3 left-3 flex items-center pointer-events-none">
            <Icon className="text-gray-400" size={18} />
          </div>
        ) : null}

        <textarea
          rows={rows}
          value={value ?? ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full ${
            Icon ? "pl-10" : "pl-3"
          } pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all ${
            disabled ? "opacity-70 cursor-not-allowed" : ""
          }`}
        />

        {clearable && !disabled && value ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onChange({ target: { value: "" } });
            }}
            className="absolute top-3 right-3 flex items-center text-gray-400 hover:text-white"
            title="Clear"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <FieldError error={error} />
    </div>
  );
}

function ColorField({ label, value, onChange, error }) {
  return (
    <div>
      <label className="text-sm font-medium text-white mb-2 block">
        {label}
      </label>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Palette className="text-gray-400" size={18} />
          </div>

          <input
            type="text"
            value={value ?? ""}
            onChange={onChange}
            placeholder="#000000"
            className="w-full pl-10 pr-10 bg-[#292826] placeholder:text-gray-500 py-3 text-sm border border-zinc-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
          />

          {value ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onChange({ target: { value: "" } });
              }}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
              title="Clear"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>

        <input
          type="color"
          value={value || "#000000"}
          onChange={onChange}
          className="w-14 h-12 p-1 bg-[#292826] border border-zinc-600 rounded-lg cursor-pointer"
          title={label}
        />
      </div>

      <FieldError error={error} />
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
  leftTextOn = "Enabled",
  leftTextOff = "Disabled",
  error,
}) {
  return (
    <div>
      <label className="text-sm font-medium text-white mb-2 block">
        {label}
      </label>

      <div className="h-[50px] px-4 bg-[#292826] border border-zinc-600 rounded-lg flex items-center justify-between">
        <div className="text-sm text-zinc-200">
          {value ? leftTextOn : leftTextOff}
        </div>

        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-amber-600"></div>
        </label>
      </div>

      <FieldError error={error} />
    </div>
  );
}

function PageLoader() {
  return (
    <div className="bg-[#1c1b19] border border-zinc-700 rounded-xl p-10 mt-6">
      <div className="flex flex-col items-center justify-center gap-3 text-zinc-300">
        <Loader2 className="animate-spin" size={28} />
        <span className="text-sm">Loading landing page data...</span>
      </div>
    </div>
  );
}

function LandingPageManagement() {
  const navigate = useNavigate();
  const API_PREFIX = process.env.REACT_APP_API_PREFIX;

  const api = useMemo(() => {
    const baseURL = (API_PREFIX || "").replace(/\/+$/, "");
    const instance = axios.create({ baseURL });

    instance.interceptors.request.use((config) => {
      const userToken = localStorage.getItem("token");
      config.headers = config.headers || {};
      config.headers["Screen-Name"] = "/landingpagemanagement";
      if (userToken) config.headers.Authorization = `Bearer ${userToken}`;
      return config;
    });

    return instance;
  }, [API_PREFIX]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [errors, setErrors] = useState(null);

  // Base company (mantém os campos exigidos pelo backend no update)
  const [baseCompany, setBaseCompany] = useState(null);

  // Campos da tela Landing Page Data
  const [form, setForm] = useState({
    landing_slug: "",
    landing_enabled: false,
  });

  const [landingConfig, setLandingConfig] = useState(
    ensureLandingConfigShape({})
  );

  const setFormField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setLandingField = (field, value) => {
    setLandingConfig((prev) => ({ ...prev, [field]: value }));
  };

  // ✅ Reviews helpers
  const reviewsList = Array.isArray(landingConfig?.reviews)
    ? landingConfig.reviews
    : [];

  const setReviewField = (index, field, value) => {
    setLandingConfig((prev) => {
      const list = Array.isArray(prev?.reviews) ? [...prev.reviews] : [];
      const current = list[index] || { author_name: "", rating: 5, text: "" };
      list[index] = { ...current, [field]: value };
      return { ...prev, reviews: list };
    });
  };

  const addReview = () => {
    setLandingConfig((prev) => {
      const list = Array.isArray(prev?.reviews) ? [...prev.reviews] : [];
      list.push({ author_name: "", rating: 5, text: "" });
      return { ...prev, reviews: list };
    });
  };

  const removeReview = (index) => {
    setLandingConfig((prev) => {
      const list = Array.isArray(prev?.reviews) ? [...prev.reviews] : [];
      const next = list.filter((_, i) => i !== index);
      return { ...prev, reviews: next };
    });
  };

  useEffect(() => {
    const boot = async () => {
      setLoading(true);

      try {
        const auth = getAuthContextFromToken();

        if (!auth?.token) {
          Swal.fire({
            icon: "error",
            title: "Unauthorized",
            text: "You need to login again.",
          });
          navigate("/login");
          return;
        }

        if (![1, 4].includes(Number(auth.userRoleId))) {
          Swal.fire({
            icon: "error",
            title: "Access denied",
            text: "Only Admin or Company Owner can access Landing settings.",
          });
          navigate("/dashboard");
          return;
        }

        if (!auth.partnerCompanyId) {
          Swal.fire({
            icon: "error",
            title: "Company not found",
            text: "Could not identify your company from the current token.",
          });
          navigate("/dashboard");
          return;
        }

        setCompanyId(auth.partnerCompanyId);

        const res = await api.get(`/partnercompany/${auth.partnerCompanyId}`);
        const data = unwrapApiData(res);

        if (!data) {
          throw new Error("Company data not found.");
        }

        setBaseCompany(data);

        setForm({
          landing_slug: data?.landing_slug ?? "",
          landing_enabled: asBool(data?.landing_enabled),
        });

        const parsedLandingConfig = ensureLandingConfigShape(
          safeJsonParse(data?.landing_config)
        );
        setLandingConfig(parsedLandingConfig);
      } catch (err) {
        const payload = extractErrorPayload(err);

        Swal.fire({
          icon: "error",
          title: "Failed to load",
          text:
            payload?.message?.toString?.() ||
            payload?.data?.toString?.() ||
            err?.message ||
            "Could not load landing page data.",
        });
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, [api, navigate]);

  const validateClient = () => {
    const nextErrors = {};

    if (!String(baseCompany?.partner_company_name || "").trim()) {
      nextErrors.partner_company_name = ["partner_company_name is missing."];
    }

    if (!String(baseCompany?.email || "").trim()) {
      nextErrors.email = [
        "Company email is missing (required by backend update).",
      ];
    }

    if (!String(baseCompany?.address || "").trim()) {
      nextErrors.address = [
        "Company address is missing (required by backend update).",
      ];
    }

    if (!String(baseCompany?.city || "").trim()) {
      nextErrors.city = ["Company city is missing (required by backend update)."];
    }

    if (!String(baseCompany?.state || "").trim()) {
      nextErrors.state = [
        "Company state is missing (required by backend update).",
      ];
    }

    if (!String(baseCompany?.zipcode || "").trim()) {
      nextErrors.zipcode = [
        "Company zipcode is missing (required by backend update).",
      ];
    }

    if (!String(baseCompany?.dot || "").trim()) {
      nextErrors.dot = ["Company DOT is missing (required by backend update)."];
    }

    setErrors(Object.keys(nextErrors).length ? nextErrors : null);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors(null);

    if (!companyId || !baseCompany) return;

    if (!validateClient()) {
      Swal.fire({
        icon: "error",
        title: "Validation error",
        text: "Please review the highlighted fields.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const landingConfigToSave = normalizeLandingConfigForSave(landingConfig);

      const payload = {
        partner_company_name: baseCompany?.partner_company_name ?? "",
        phone: baseCompany?.phone ?? "",
        email: baseCompany?.email ?? "",
        address: baseCompany?.address ?? "",
        city: baseCompany?.city ?? "",
        state: baseCompany?.state ?? "",
        zipcode: baseCompany?.zipcode ?? "",
        dot: baseCompany?.dot ?? "",
        is_allowed_to_operate:
          baseCompany?.is_allowed_to_operate === null ||
          baseCompany?.is_allowed_to_operate === undefined
            ? ""
            : String(baseCompany?.is_allowed_to_operate),

        landing_slug: form.landing_slug ?? "",
        landing_enabled: form.landing_enabled ? 1 : 0,

        landing_config: JSON.stringify(landingConfigToSave ?? {}),
      };

      const res = await api.post(`/partnercompany/landing/${companyId}`, payload);
      const updated = unwrapApiData(res);

      if (updated) {
        setBaseCompany(updated);
        setForm((prev) => ({
          ...prev,
          landing_slug: updated?.landing_slug ?? prev.landing_slug,
          landing_enabled: asBool(updated?.landing_enabled),
        }));
        setLandingConfig(
          ensureLandingConfigShape(safeJsonParse(updated?.landing_config))
        );
      }

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Landing page data updated successfully.",
      });
    } catch (err) {
      const payload = extractErrorPayload(err);
      const serverErrors = parseErrorMap(payload);
      if (serverErrors) setErrors(serverErrors);

      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          payload?.message?.toString?.() ||
          payload?.data?.toString?.() ||
          "Unexpected error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const knownLandingKeys = [
    "email",
    "phone",
    "partner_company_name",
    "landing_headline",
    "landing_subheadline",
    "form_quote_headline",
    "form_banner_enabled",
    "form_badge_enabled",
    "landing_logo_url",
    "landing_video_url",
    "landing_favicon_url",
    "landing_primary_color",
    "landing_primary_hover_color",
    "landing_secondary_color",
    "landing_secondary_hover_color",
    "landing_background_image_url",
    "reviews",
  ];

  const extraLandingEntries = Object.entries(landingConfig || {}).filter(
    ([key]) => !knownLandingKeys.includes(key)
  );

  const bannerEnabledBool = asBool(landingConfig?.form_banner_enabled);
  const badgeEnabledBool = asBool(landingConfig?.form_badge_enabled);

  const normalizedLandingSlug = String(form.landing_slug || "").trim().replace(/^\/+|\/+$/g, "");
  const landingPageUrl = normalizedLandingSlug
    ? `https://quotes.cajuu.app/${normalizedLandingSlug}`
    : "";

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <div className="flex items-center justify-between gap-3">
            <InfoSection
              title="LANDING PAGE DATA"
              description="Edit landing page settings and branding fields"
            />
          </div>
        </GroupedContainer>

        {loading ? (
          <PageLoader />
        ) : (
          <form onSubmit={submit} className="space-y-8 mt-6">
            {/* ===== Landing Settings ===== */}
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  LANDING SETTINGS
                </h2>
                <div className="h-px w-full bg-zinc-700"></div>
              </div>

              <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-2">
                    <TextField
                      label="Landing Slug"
                      value={form.landing_slug}
                      onChange={(e) =>
                        setFormField("landing_slug", e.target.value)
                      }
                      placeholder="company-slug"
                      Icon={Globe}
                      error={errors?.landing_slug}
                      disabled
                      clearable={false}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ToggleField
                      label="Landing Enabled"
                      value={!!form.landing_enabled}
                      onChange={(checked) =>
                        setFormField("landing_enabled", checked)
                      }
                      leftTextOn="Enabled"
                      leftTextOff="Disabled"
                      error={errors?.landing_enabled}
                    />
                  </div>

                  <div className="lg:col-span-4">
                    <label className="text-sm font-medium text-white mb-2 block">
                      Landing Page Link
                    </label>

                    {landingPageUrl ? (
                      <a
                        href={landingPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 w-full bg-[#292826] border border-zinc-600 hover:border-amber-500 rounded-lg px-4 py-3 transition-all"
                        title="Open landing page in a new tab"
                      >
                        <span className="flex items-center gap-3 min-w-0">
                          <span className="shrink-0 w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 group-hover:bg-amber-500/20 transition-colors">
                            <ExternalLink size={18} />
                          </span>

                          <span className="min-w-0">
                            <span className="block text-xs text-zinc-400 mb-0.5">
                              Public landing page
                            </span>
                            <span className="block text-sm text-amber-300 group-hover:text-amber-200 break-all">
                              {landingPageUrl}
                            </span>
                          </span>
                        </span>

                        <span className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-zinc-900 bg-amber-500 group-hover:bg-amber-400 rounded-lg px-3 py-2 transition-colors shrink-0">
                          Open
                          <ExternalLink size={14} />
                        </span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 w-full bg-[#292826] border border-zinc-600 rounded-lg px-4 py-3 text-sm text-zinc-400">
                        <ExternalLink size={18} className="text-zinc-500 shrink-0" />
                        The landing page link will appear after a landing slug is available.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Landing Config JSON (individual fields) ===== */}
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  LANDING CONFIG (JSON FIELDS)
                </h2>
                <div className="h-px w-full bg-zinc-700"></div>
              </div>

              <div className="bg-[#1c1b19] p-6 rounded-xl border border-zinc-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-2">
                    <TextField
                      label="partner_company_name"
                      value={landingConfig.partner_company_name}
                      onChange={(e) =>
                        setLandingField("partner_company_name", e.target.value)
                      }
                      placeholder="Brand name for landing"
                      Icon={Type}
                      error={errors?.["landing_config.partner_company_name"]}
                    />
                  </div>

                  <div>
                    <TextField
                      label="email"
                      value={landingConfig.email}
                      onChange={(e) => setLandingField("email", e.target.value)}
                      placeholder="support@company.com"
                      Icon={Mail}
                      error={errors?.["landing_config.email"]}
                    />
                  </div>

                  <div>
                    <TextField
                      label="phone"
                      value={landingConfig.phone}
                      onChange={(e) => setLandingField("phone", e.target.value)}
                      placeholder="+1 (000) 000-0000"
                      Icon={Phone}
                      error={errors?.["landing_config.phone"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_headline"
                      value={landingConfig.landing_headline}
                      onChange={(e) =>
                        setLandingField("landing_headline", e.target.value)
                      }
                      placeholder="Professional Auto Transport Services"
                      Icon={Type}
                      error={errors?.["landing_config.landing_headline"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_subheadline"
                      value={landingConfig.landing_subheadline}
                      onChange={(e) =>
                        setLandingField("landing_subheadline", e.target.value)
                      }
                      placeholder="Reliable and safe transportation solutions for all your needs"
                      Icon={Type}
                      error={errors?.["landing_config.landing_subheadline"]}
                    />
                  </div>

                  <div className="lg:col-span-4">
                    <TextField
                      label="form_quote_headline"
                      value={landingConfig.form_quote_headline}
                      onChange={(e) =>
                        setLandingField("form_quote_headline", e.target.value)
                      }
                      placeholder="Get your quote now or contact us directly!"
                      Icon={Type}
                      error={errors?.["landing_config.form_quote_headline"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ToggleField
                      label="form_banner_enabled"
                      value={bannerEnabledBool}
                      onChange={(checked) =>
                        setLandingField("form_banner_enabled", checked)
                      }
                      leftTextOn="On"
                      leftTextOff="Off"
                      error={errors?.["landing_config.form_banner_enabled"]}
                    />
                    <div className="text-xs text-zinc-400 mt-2 flex items-center gap-2">
                      <Flag size={14} className="text-zinc-400" />
                      Stored as <code>"true"</code>/<code>"false"</code> in JSON
                      on save.
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <ToggleField
                      label="form_badge_enabled"
                      value={badgeEnabledBool}
                      onChange={(checked) =>
                        setLandingField("form_badge_enabled", checked)
                      }
                      leftTextOn="On"
                      leftTextOff="Off"
                      error={errors?.["landing_config.form_badge_enabled"]}
                    />
                    <div className="text-xs text-zinc-400 mt-2 flex items-center gap-2">
                      <BadgeCheck size={14} className="text-zinc-400" />
                      Stored as <code>"true"</code>/<code>"false"</code> in JSON
                      on save.
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_logo_url"
                      value={landingConfig.landing_logo_url}
                      onChange={(e) =>
                        setLandingField("landing_logo_url", e.target.value)
                      }
                      placeholder="https://..."
                      Icon={Image}
                      error={errors?.["landing_config.landing_logo_url"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_video_url"
                      value={landingConfig.landing_video_url}
                      onChange={(e) =>
                        setLandingField("landing_video_url", e.target.value)
                      }
                      placeholder="https://..."
                      Icon={Video}
                      error={errors?.["landing_config.landing_video_url"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_favicon_url"
                      value={landingConfig.landing_favicon_url}
                      onChange={(e) =>
                        setLandingField("landing_favicon_url", e.target.value)
                      }
                      placeholder="https://..."
                      Icon={Image}
                      error={errors?.["landing_config.landing_favicon_url"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <TextField
                      label="landing_background_image_url"
                      value={landingConfig.landing_background_image_url}
                      onChange={(e) =>
                        setLandingField(
                          "landing_background_image_url",
                          e.target.value
                        )
                      }
                      placeholder="https://..."
                      Icon={Image}
                      error={
                        errors?.["landing_config.landing_background_image_url"]
                      }
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ColorField
                      label="landing_primary_color"
                      value={landingConfig.landing_primary_color}
                      onChange={(e) =>
                        setLandingField("landing_primary_color", e.target.value)
                      }
                      error={errors?.["landing_config.landing_primary_color"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ColorField
                      label="landing_primary_hover_color"
                      value={landingConfig.landing_primary_hover_color}
                      onChange={(e) =>
                        setLandingField(
                          "landing_primary_hover_color",
                          e.target.value
                        )
                      }
                      error={
                        errors?.["landing_config.landing_primary_hover_color"]
                      }
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ColorField
                      label="landing_secondary_color"
                      value={landingConfig.landing_secondary_color}
                      onChange={(e) =>
                        setLandingField(
                          "landing_secondary_color",
                          e.target.value
                        )
                      }
                      error={errors?.["landing_config.landing_secondary_color"]}
                    />
                  </div>

                  <div className="lg:col-span-2">
                    <ColorField
                      label="landing_secondary_hover_color"
                      value={landingConfig.landing_secondary_hover_color}
                      onChange={(e) =>
                        setLandingField(
                          "landing_secondary_hover_color",
                          e.target.value
                        )
                      }
                      error={
                        errors?.["landing_config.landing_secondary_hover_color"]
                      }
                    />
                  </div>

                  {/* ✅ REVIEWS EDITOR */}
                  <div className="lg:col-span-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="text-sm font-semibold text-zinc-200">
                          Reviews (Testimonials)
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          Add, edit or remove customer reviews shown on the
                          landing page. If this list is empty, the testimonials
                          section will be hidden.
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={addReview}
                        className="inline-flex items-center gap-2 text-xs px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-900 border border-zinc-700 transition-colors text-white"
                      >
                        <Plus size={16} />
                        Add Review
                      </button>
                    </div>

                    {reviewsList.length === 0 ? (
                      <div className="bg-[#141311] border border-zinc-700 rounded-xl p-4 text-sm text-zinc-300">
                        No reviews configured. The landing page will hide the
                        Testimonials section.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {reviewsList.map((review, index) => (
                          <div
                            key={index}
                            className="bg-[#141311] border border-zinc-700 rounded-xl p-4"
                          >
                            <div className="flex items-center justify-between gap-3 mb-4">
                              <div className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
                                <Star size={16} className="text-amber-400" />
                                Review #{index + 1}
                              </div>

                              <button
                                type="button"
                                onClick={() => removeReview(index)}
                                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-red-900/40 bg-red-950/30 hover:bg-red-950/50 transition-colors text-red-200"
                                title="Remove review"
                              >
                                <Trash2 size={16} />
                                Remove
                              </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="md:col-span-1">
                                <TextField
                                  label="Author Name"
                                  value={review?.author_name ?? ""}
                                  onChange={(e) =>
                                    setReviewField(
                                      index,
                                      "author_name",
                                      e.target.value
                                    )
                                  }
                                  placeholder="John D."
                                  Icon={Type}
                                  clearable
                                />
                              </div>

                              <div className="md:col-span-1">
                                <TextField
                                  label="Rating (1-5)"
                                  type="number"
                                  value={review?.rating ?? 5}
                                  onChange={(e) => {
                                    // Permite apagar (string vazia) durante digitação
                                    const raw = String(e.target.value ?? "");

                                    // Se estiver vazio, mantém vazio (ou você pode forçar 1)
                                    if (raw === "") {
                                      setReviewField(index, "rating", "");
                                      return;
                                    }

                                    // Remove qualquer coisa que não seja dígito
                                    const digitsOnly = raw.replace(/[^\d]/g, "");

                                    // Se ficou vazio após limpar, não atualiza
                                    if (!digitsOnly) {
                                      setReviewField(index, "rating", "");
                                      return;
                                    }

                                    // Converte e força inteiro
                                    let n = parseInt(digitsOnly, 10);
                                    if (Number.isNaN(n)) n = 5;

                                    // Clampa 1..5
                                    if (n < 1) n = 1;
                                    if (n > 5) n = 5;

                                    setReviewField(index, "rating", n);
                                  }}
                                  onKeyDown={(e) => {
                                    // Bloqueia notação científica, sinais e ponto/flutuante
                                    const blocked = ["e", "E", "+", "-", ".", ","];
                                    if (blocked.includes(e.key)) e.preventDefault();
                                  }}
                                  onBlur={(e) => {
                                    // No blur, garante que não ficou vazio
                                    const raw = String(e.target.value ?? "").trim();
                                    if (raw === "") {
                                      setReviewField(index, "rating", 5);
                                      return;
                                    }

                                    let n = parseInt(raw, 10);
                                    if (Number.isNaN(n)) n = 5;
                                    if (n < 1) n = 1;
                                    if (n > 5) n = 5;

                                    setReviewField(index, "rating", n);
                                  }}
                                  placeholder="5"
                                  Icon={Star}
                                  clearable={false}
                                  min={1}
                                  max={5}
                                  step={1}
                                  inputMode="numeric"
                                />
                                <div className="text-xs text-zinc-500 mt-2">Use values from 1 to 5.</div>
                              </div>

                              <div className="md:col-span-3">
                                <TextAreaField
                                  label="Comment"
                                  value={review?.text ?? ""}
                                  onChange={(e) =>
                                    setReviewField(index, "text", e.target.value)
                                  }
                                  placeholder="Write the customer comment..."
                                  Icon={Type}
                                  rows={4}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {extraLandingEntries.length > 0 && (
                    <div className="lg:col-span-4">
                      <div className="text-sm font-semibold text-zinc-200 mb-3">
                        Extra JSON fields
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {extraLandingEntries.map(([key, val]) => (
                          <TextField
                            key={key}
                            label={key}
                            value={
                              typeof val === "string"
                                ? val
                                : JSON.stringify(val)
                            }
                            onChange={(e) =>
                              setLandingField(key, e.target.value)
                            }
                            placeholder={key}
                            Icon={Link}
                            error={errors?.[`landing_config.${key}`]}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ===== Actions ===== */}
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg border border-zinc-700 hover:bg-zinc-900 transition-colors text-white font-medium"
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 text-sm px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 transition-colors text-white font-medium disabled:opacity-60"
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                {submitting ? "Saving..." : "Save Landing Data"}
              </button>
            </div>
          </form>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default LandingPageManagement;